import { SagaIterator } from 'redux-saga';
import { call, delay, race, select, take, takeLeading } from 'redux-saga/effects';
import { GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { DashboardActions } from '../dashboard.actions';
import { WarenkorbAngebot } from '../reducers/produkte.reducer';
import { selectTgvChildrenWithChangeableLaufzeit } from '../selectors/tiergruppeWarenkorb.selector';
import { selectTarifierungIsInProgress } from '../selectors/transient-state.selector';
import { ONE_YEAR_LAUFZEIT } from '../selectors/vertragslaufzeit.selector';

export const TGV_LAUFZEIT_TIMEOUT_MS = 30000;

export function* watchTgvGroupLaufzeit(): SagaIterator {
  yield takeLeading(
    DashboardActions.TGV_GROUP_LAUFZEIT_REQUESTED,
    safeSaga(tgvGroupLaufzeitHandler, App.SHOP_DASHBOARD),
  );
}

interface TarifierungResult {
  ended?: unknown;
  failed?: unknown;
}

function* waitForTarifierung(): SagaIterator<TarifierungResult> {
  return yield race({
    ended: take(GlobalActions.ALL_TARIFIERUNGEN_HAVE_ENDED),
    failed: take(GlobalActions.TARIFIERE_SHOP_FAILED),
    timeout: delay(TGV_LAUFZEIT_TIMEOUT_MS),
  });
}

function logStop(child: WarenkorbAngebot, result: TarifierungResult): void {
  PS20Logger.info(
    App.SHOP_DASHBOARD,
    `Group Laufzeit change stopped at ${child.appId}/${child.angebotsId}: ${result.failed ? 'tarifierung failed' : 'tarifierung timed out'}`,
  );
}

export function* tgvGroupLaufzeitHandler(): SagaIterator {
  const children: WarenkorbAngebot[] = yield select(selectTgvChildrenWithChangeableLaufzeit);

  for (const child of children) {
    const tarifierungIsInProgress: boolean = yield select(selectTarifierungIsInProgress);
    if (tarifierungIsInProgress) {
      const running: TarifierungResult = yield call(waitForTarifierung);
      if (!running.ended) {
        logStop(child, running);
        return;
      }
    }

    const stillEligible: WarenkorbAngebot[] = yield select(selectTgvChildrenWithChangeableLaufzeit);
    const isStillEligible = stillEligible.some(
      eligible => eligible.appId === child.appId && eligible.angebotsId === child.angebotsId,
    );
    if (!isStillEligible) {
      continue;
    }

    yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().vertragslaufzeitAusgewaehlt({
        appId: child.appId,
        angebotsId: child.angebotsId,
        vertragslaufzeit: ONE_YEAR_LAUFZEIT,
      }),
    );
    const result: TarifierungResult = yield call(waitForTarifierung);
    if (!result.ended) {
      logStop(child, result);
      return;
    }
  }
}
